import { NamedRoutes } from '@/types';
import {
  Button,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Stack,
  VisuallyHidden,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faSquareArrowRight } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { WorkflowAccessLevel, Workflows, DropdownOptions } from 'types';
import { ApplicationState } from '../../../../store';
import { UserRole } from '../../../../store/auth/types';
import { hasPerm, P } from '../../../../utilities/roles/permissions';
import { Tabs } from './tabs';
import { workflowOptions } from '@/utilities/helpers';
import { getPartnerAutoNavigationPath, shouldShowItemConcise, buildAdmissionsParams } from '../helpers';
import { useTranslation } from "react-i18next";

const selectUserRole = (state: ApplicationState) => state.auth.role;
const selectWorkflowAccess = (state: ApplicationState) => state.auth.currentUser?.workflowAccess;
const selectApplyEnabled = (state: ApplicationState) => state.config?.applyEnabled === true;
const selectAuthAgencyData = (state: ApplicationState) => state.auth.authAgencyData;
const selectConfigUrls = (state: ApplicationState) => state.config.urls || {};

const AGENT_ROLES = [UserRole.Agent, UserRole.AgencyAdmin];

export const LeftBar = () => {
    const { t } = useTranslation('common');
  const userRole = useSelector(selectUserRole);
  const workflowAccess = useSelector(selectWorkflowAccess);
  const canAccessApply = useSelector(selectApplyEnabled);
  const authAgencyData = useSelector(selectAuthAgencyData);
  const configUrls = useSelector(selectConfigUrls);

  const navigate = useNavigate();
  const location = useLocation();

  const isAgentUser = useMemo(() => AGENT_ROLES.includes(userRole), [userRole]);
  const partnerFullAccess = useMemo(() => workflowAccess === WorkflowAccessLevel.FullAccess, [workflowAccess]);
  const canViewNotInvited = useMemo(() => hasPerm(userRole, P.NOT_INVITED), [userRole]);

  const showWorkflowOptions = useMemo(() => {
    if (!authAgencyData?.assignedUnis?.length) {
      return false;
    }

    const blockList = authAgencyData.blockCreateApplicationUnis || [];
    const isBlockedAll = Array.isArray(blockList) ? blockList.includes('ALL') : blockList === 'ALL';

    if (isBlockedAll) {
      return false;
    }

    const filteredAssignedUnis = authAgencyData.assignedUnis.filter(
      uni => !Array.isArray(blockList) || !blockList.includes(uni),
    );

    if (filteredAssignedUnis?.length === 0) {
      return false;
    }

    const configUrlKeys = Object.keys(configUrls);
    if (configUrlKeys.length === 0) {
      return false;
    }

    return filteredAssignedUnis.some(uni => configUrlKeys.includes(uni));
  }, [authAgencyData?.assignedUnis, authAgencyData?.blockCreateApplicationUnis, configUrls]);

  const filteredWorkflowOptions = useMemo(() => {
    return canAccessApply || showWorkflowOptions || partnerFullAccess
      ? workflowOptions
      : workflowOptions.filter(workflow => workflow.value !== 'Admissions');
  }, [canAccessApply, showWorkflowOptions, partnerFullAccess]);

  const currentWorkflow = useMemo(() => {
    if (location.pathname === NamedRoutes.ADMISSIONS_WORKFLOW) {
      return Workflows.ADMISSIONS;
    }
    if (location.pathname === NamedRoutes.ROOT || location.pathname === '/') {
      return Workflows.CAS;
    }
    return Workflows.CAS;
  }, [location.pathname]);

  const targetPath = useMemo(
    () => getPartnerAutoNavigationPath(userRole, workflowAccess, canAccessApply, showWorkflowOptions),
    [userRole, workflowAccess, canAccessApply, showWorkflowOptions],
  );

  const canShowWorkflowButton = useMemo(
    () => shouldShowItemConcise(userRole, partnerFullAccess),
    [userRole, partnerFullAccess],
  );

  const navigateToWorkflow = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const workflowType = e.currentTarget.textContent;

      switch (workflowType) {
        case Workflows.ADMISSIONS: {
          if (location.pathname === NamedRoutes.ADMISSIONS_WORKFLOW) return;

          const params = buildAdmissionsParams(isAgentUser);
          navigate({ pathname: NamedRoutes.ADMISSIONS_WORKFLOW, search: params.toString() });
          break;
        }
        case Workflows.CAS: {
          if (location.pathname === NamedRoutes.ROOT) return;
          navigate(NamedRoutes.ROOT);
          break;
        }
      }
    },
    [location.pathname, navigate, isAgentUser],
  );

  // Effect for auto-navigation
  useEffect(() => {
    if (targetPath && location.pathname !== targetPath) {
      if (targetPath === NamedRoutes.ADMISSIONS_WORKFLOW) {
        const params = buildAdmissionsParams(isAgentUser);
        navigate({ pathname: NamedRoutes.ADMISSIONS_WORKFLOW, search: params.toString() });
      } else {
        navigate(targetPath);
      }
    }
  }, [targetPath, location.pathname, navigate, isAgentUser]);

  return (
    <>
      {canShowWorkflowButton && (
        <Stack spacing={0} pt={{ base: 0, md: 4 }}>
          <Menu>
            <MenuButton
              as={Button}
              variant="secondary"
              textAlign="left"
              leftIcon={<FontAwesomeIcon className="utility-icon" icon={faSquareArrowRight} />}
              rightIcon={<FontAwesomeIcon className="utility-icon" icon={faAngleDown} />}
              data-selector="workflow-dropdown"
              mx="sidebarPxSmall"
            >
              <VisuallyHidden>{t('common:workflow')} </VisuallyHidden>
              {currentWorkflow}
            </MenuButton>

            <MenuList minWidth="180px">
              <MenuOptionGroup defaultValue={currentWorkflow} type="radio">
                {filteredWorkflowOptions.map((option: DropdownOptions) => (
                  <MenuItemOption key={option.value} value={option.value} onClick={navigateToWorkflow}>
                    {option.label}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </MenuList>
          </Menu>
        </Stack>
      )}

      <Stack
        spacing={6}
        className="nav-scrollbar"
        overflowY={{ base: undefined, md: 'auto' }}
        px={'sidebarPxSmall'}
        py={6}
      >
        <Tabs canViewNotInvitedTabs={canViewNotInvited} />
      </Stack>
    </>
  );
};
